import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  IconCoffee,
  IconCarambola,
  IconTrendingUp,
  IconTrendingDown,
  IconArrowRight,
  IconLayoutSidebarRight,
  IconProps,
  IconPhotoOff,
} from "@tabler/icons-react";
import OverviewSectionWrapper from "../section/OverviewSectionWrapper";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import CreativeDetailsDialog from "./CreativeDetailsDialog";
import {
  CreativeOverviewData,
  CreativeOverviewRow,
  MetricData,
} from "@/screens/creatives/types";
import ATRAction from "@/screens/creatives/components/Overview/card-actions/ATRAction";
import WaveLoader from "@/components/common/loaders/WaveLoader";
import Media from "@/components/common/adCard/Media";
import ShowDetailsAction from "../card-actions/ShowDetailsAction";
import ErrorState from "@/components/common/errorStates/ErrorState";
import NothingToShow from "@/components/common/emptyStates/NothingToShow";
import { CreativeReportMetadata } from "@/types";
import { useGlobalStore } from "@/GlobalStore";
import MetricCard from "../cards/MetricCard";

type SelectedTab = string;

interface Props {
  data?: CreativeOverviewData;
  title: string;
  loading: boolean;
  error?: { hasError: boolean; message?: string };
  defaultMetricId?: string;
  onSetDefaultMetric: (metricId: string) => void;
  onRetry?: () => void;
  reportMetadata?: CreativeReportMetadata;
}

const CreativeOverview: React.FC<Props> = ({
  data,
  title,
  loading = true,
  error,
  defaultMetricId,
  onSetDefaultMetric,
  onRetry,
  reportMetadata,
}) => {
  const [selectedTab, setSelectedTab] = useState<SelectedTab | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<string>(defaultMetricId || "");
  const [selectedCreative, setSelectedCreative] = useState<CreativeOverviewRow | null>(
    null,
  );
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [gridColumns, setGridColumns] = useState(0);
  const [gridInnerWidth, setGridInnerWidth] = useState(0);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const leftColumnRef = useRef<HTMLDivElement>(null);

  // Static key for image cache-busting (aligns with Media.tsx)
  const imageKey = 1754068657945;

  // Get available tabs from data keys with custom ordering
  const availableTabs = useMemo(() => {
    const keys = Object.keys(data?.trends_data ?? {});
    const orderMap: Record<string, number> = {
      best: 0,
      scaling: 1,
      declining: 2,
    };
    return keys.sort((a, b) => (orderMap[a] ?? 999) - (orderMap[b] ?? 999));
  }, [data]);

  const activeTab = selectedTab ?? availableTabs[0] ?? null;
  const activeCreative = selectedCreative ?? data?.trends_data?.[activeTab ?? ""]?.rows?.[0] ?? null;
  const selectedFirstAd = activeCreative?.ads?.[0] ?? activeCreative?.first_ad_metadata;
  const selectedCreativeType =
    selectedFirstAd?.creative?.videos?.length &&
      selectedFirstAd?.creative?.videos?.length > 0 &&
      selectedFirstAd?.creative?.videos?.[0]?.permalink_url
      ? "video"
      : activeCreative?.creative_type === "video"
        ? "video"
        : "image";

  const selectTab = (tab: string) => {
    setSelectedTab(tab);
    setSelectedCreative(data?.trends_data?.[tab]?.rows?.[0] ?? null);
    setImageErrors(new Set());
  };

  useEffect(() => {
    if (defaultMetricId) {
      setSelectedMetric(defaultMetricId);
    }
  }, [defaultMetricId]);

  // Monitor grid width for responsive layout and column calculation
  useEffect(() => {
    const computeGridSizing = () => {
      if (!containerRef.current || !leftColumnRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      const leftColumnWidth = leftColumnRef.current.offsetWidth;
      const availableWidth = containerWidth - leftColumnWidth - 24;
      const columnWidth = 120; // px
      const columnGap = 6; // px

      // Given width >= n*W + (n-1)*G → n <= (width + G) / (W + G)
      const maxColumnsByWidth = Math.max(
        0,
        Math.floor((availableWidth + columnGap) / (columnWidth + columnGap)),
      );

      // Get the actual number of grid items
      const actualGridItems = getCreativeGrid().length;

      // Cap the grid columns to the actual number of items
      const maxColumns = Math.max(0, Math.min(maxColumnsByWidth, actualGridItems));

      setGridColumns(maxColumns);
      const requiredWidth = maxColumns * columnWidth + (maxColumns - 1) * columnGap;
      setGridInnerWidth(requiredWidth);
    };

    // Initial measurement after layout
    const timer = setTimeout(() => computeGridSizing(), 100);

    // Observe grid size changes
    const resizeObserver = new ResizeObserver(() => computeGridSizing());
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Fallback to window resize
    window.addEventListener("resize", computeGridSizing);

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
      window.removeEventListener("resize", computeGridSizing);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, loading]);

  const handleMetricChange = (metric: string) => {
    setSelectedMetric(metric);
  };

  const handleCreativeSelect = (creative: CreativeOverviewRow) => {
    setSelectedCreative(creative);
  };

  const handleImageError = (id: string | undefined) => {
    if (id !== undefined) {
      setImageErrors((prev) => new Set(prev).add(id));
    }
  };

  // Get current tab data
  const currentTabData = activeTab ? data?.trends_data?.[activeTab] : null;

  // Generate metric cards data from columns
  const getMetricCardsData = (): MetricData[] => {
    if (!currentTabData?.columns || !activeCreative) return [];

    return currentTabData.columns.map((column) => {
      const valueKey = column.is_kpi ? selectedMetric || "" : column.key;
      const metricColumn = column.is_kpi
        ? (data?.creative_metric_columns?.find((col) => col.key === valueKey) ?? column)
        : column;
      const changeKey = `${valueKey}_percent_change`;
      const value = activeCreative[
        valueKey as keyof CreativeOverviewRow
      ] as MetricData["value"];
      const percentChange = activeCreative[
        changeKey as keyof CreativeOverviewRow
      ] as MetricData["percentChange"];

      return {
        key: valueKey,
        label: metricColumn.label,
        value: value,
        prefix: metricColumn.prefix,
        suffix: metricColumn.suffix,
        percentChange: percentChange,
        hasDropdown: column.is_kpi || false,
      };
    });
  };

  const metricCardsData: MetricData[] = getMetricCardsData();

  const aiSuggestions = [
    { text: "Find similar hooks based on this ad", icon: "🌠" },
    { text: "Transform Coming Soon into a static", icon: "🩻" },
    { text: "Re-imagine this ad in different format", icon: "🏞️" },
  ];

  // Get creative grid from current tab data
  const getCreativeGrid = (): CreativeOverviewRow[] => {
    if (!currentTabData?.rows || currentTabData.rows.length === 0) {
      return [];
    }

    return currentTabData.rows.slice(0, 12);
  };

  const creativeGrid = getCreativeGrid();
  const isCompactView = (gridColumns < 3 && creativeGrid.length >= 3) || gridColumns < 1;

  // Get icon for tab
  const getTabIcon = (tabKey: string) => {
    const iconMap: Record<string, React.ComponentType<IconProps>> = {
      best: IconCarambola,
      scaling: IconTrendingUp,
      declining: IconTrendingDown,
    };
    return iconMap[tabKey] || IconCarambola;
  };

  const dataConfigurations = useGlobalStore((s) => s.dataConfigurations);
  const metricColumnKeys = data?.creative_metric_columns?.map((c) => c.key);
  const metricOptions = dataConfigurations?.column_choices
    ?.find((c) => c.key === "metric")
    ?.options.filter((c) => metricColumnKeys?.includes(c.key));

  return (
    <OverviewSectionWrapper
      title={title}
      titleIcon={IconCoffee}
      showInfoIcon={true}
      tooltipContent="Track how your ad creatives are performing, quickly spot winners and underperformers."
    >
      {loading ? (
        <div className="w-full h-[300px] flex items-center justify-center">
          <WaveLoader />
        </div>
      ) : error?.hasError ? (
        <div className="w-full h-[300px] flex items-center justify-center">
          <ErrorState onRetry={onRetry} />
        </div>
      ) : !data ? (
        <div className="w-full h-[300px] flex items-center justify-center">
          <NothingToShow />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Navigation Tabs */}
          <div className="flex gap-2">
            {availableTabs.map((tabKey) => {
              const tabData = data?.trends_data?.[tabKey];
              const TabIcon = getTabIcon(tabKey);

              return (
                <Button
                  variant="sq-outline"
                  size="sm"
                  key={tabKey}
                  onClick={() => selectTab(tabKey)}
                  className={`transition-colors ${activeTab === tabKey ? "squircle-outline-color-brand-orange text-brand-orange font-medium !bg-brand-orange/5" : ""}`}
                >
                  <TabIcon
                    size={14}
                    className={cn(
                      "text-brand-primary-400",
                      activeTab === tabKey ? "text-brand-orange" : "",
                    )}
                  />
                  {tabData?.label || tabKey}
                </Button>
              );
            })}
          </div>

          {/* Main Content Area - add conditional flex-wrap */}

          {creativeGrid.length === 0 ? (
            <div className="w-full h-[300px] flex items-center justify-center">
              <NothingToShow />
            </div>
          ) : (
            <div
              ref={containerRef}
              className={cn("flex gap-6 justify-between", isCompactView && "flex-col")}
            >
              <div ref={leftColumnRef} className="w-min flex gap-[23px] items-start">
                <div className="w-[300px]">
                  <Media
                    type={selectedCreativeType}
                    source={
                      selectedCreativeType === "image"
                        ? selectedFirstAd?.creative?.images[0]
                          ?.permalink_url ||
                        activeCreative?.creative_thumbnail ||
                        undefined
                        : selectedFirstAd?.creative?.videos[0]
                          ?.permalink_url ||
                        activeCreative?.creative_thumbnail ||
                        undefined
                    }
                    thumbnail={
                      selectedFirstAd?.creative?.thumbnail_url ||
                      activeCreative?.creative_thumbnail ||
                      undefined
                    }
                    onClick={() => setShowDetailsDialog(true)}
                    status="success"
                    actions={[
                      {
                        component: (
                          <ATRAction rank={activeCreative?.all_time_rank || 0} />
                        ),
                        position: "top-left",
                        visibility: "always",
                      },
                      {
                        component: (
                          <ShowDetailsAction onClick={() => setShowDetailsDialog(true)} />
                        ),
                        position: "top-right",
                        visibility: "playing",
                      },
                    ]}
                  />
                </div>
                <div className="py-[20.5px] h-[372px] flex flex-col justify-between gap-4">
                  {/* Performance Metrics */}
                  <div className="p-[1px] flex w-max rounded-[12px] squircle squircle-radius-[12px] squircle-outline-[1px] squircle-outline-color-border-gray">
                    {metricCardsData.map((metric) => (
                      <MetricCard
                        key={metric.key}
                        title={metric.hasDropdown ? undefined : metric.label}
                        value={metric.value}
                        prefix={metric.prefix}
                        suffix={metric.suffix}
                        change={metric.percentChange}
                        hasDropdown={metric.hasDropdown}
                        selectedMetric={metric.hasDropdown ? selectedMetric : undefined}
                        onMetricChange={
                          metric.hasDropdown ? handleMetricChange : undefined
                        }
                        className={"min-w-[150px]"}
                        metricOptions={metricOptions}
                        defaultMetric={defaultMetricId}
                        onSetDefault={onSetDefaultMetric}
                      />
                    ))}
                  </div>

                  {/* AI Suggestions Section */}
                  <div className="flex flex-col gap-[20px]">
                    <div className="flex items-center gap-[6px]">
                      <span className="text-[12px] font-medium text-brand-primary-new">
                        Get more ideas
                      </span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M8.63133 10.5267L8.08467 8.758M8.08467 8.758L7.62733 7.27933C7.25467 6.07533 7.06867 5.474 6.73667 5.474C6.40533 5.474 6.21867 6.07533 5.84667 7.27933L5.38867 8.758M8.08467 8.758H5.38867M4.842 10.526L5.38867 8.758M10.526 10.526V7.36867M10.526 5.78933V5.474M2 8C2 5.17133 2 3.75733 2.87867 2.87867C3.75733 2 5.172 2 8 2C10.828 2 12.2427 2 13.1213 2.87867C14 3.75733 14 5.172 14 8C14 10.828 14 12.2427 13.1213 13.1213C12.2427 14 10.828 14 8 14C5.172 14 3.75733 14 2.87867 13.1213C2 12.2427 2 10.828 2 8Z"
                          stroke="url(#paint0_linear_36964_36720)"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <defs>
                          <linearGradient
                            id="paint0_linear_36964_36720"
                            x1="1.77778"
                            y1="2.40001"
                            x2="12.4444"
                            y2="11.7333"
                            gradientUnits="userSpaceOnUse"
                          >
                            <stop stopColor="#FF4500" />
                            <stop offset="1" stopColor="#FF4B7B" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </div>
                    <div className="relative w-max flex flex-col gap-[10px]">
                      {aiSuggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-[8px] text-[12px] text-brand-primary-800"
                        >
                          <span>{suggestion.icon}</span>
                          <span>{suggestion.text}</span>
                          <span>
                            <IconArrowRight
                              size={14}
                              className="text-brand-primary-400"
                            />
                          </span>
                        </div>
                      ))}
                      <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-[rgba(255,255,255,0.78)]">
                        <div className="h-[24px] px-[6px] squircle squircle-radius-[6px] squircle-outline-[1px] squircle-fill-brand-orange bg-white">
                          <span className="text-[11px] font-medium bg-gradient-to-r from-brand-orange to-[#FF495A] bg-clip-text text-transparent">
                            Coming Soon
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Button
                      variant="sq-outline"
                      onClick={() => setShowDetailsDialog(true)}
                    >
                      <IconLayoutSidebarRight
                        size={14}
                        className="text-brand-primary-400"
                      />
                      <span className="text-[11px]">Show Details</span>
                    </Button>
                  </div>
                </div>
              </div>

              {/* Right Column - Creative Grid */}
              <div
                className={cn(
                  "flex-1 overflow-hidden flex justify-end items-start",
                  isCompactView && "justify-start",
                )}
              >
                <div
                  className={cn("flex flex-wrap gap-[6px]")}
                  style={
                    gridInnerWidth && !isCompactView
                      ? { width: `${gridInnerWidth}px` }
                      : undefined
                  }
                >
                  {creativeGrid.map((creative, index) => {
                    const firstAd = creative.ads?.[0] ?? creative.first_ad_metadata;
                    const creativeId = firstAd?.ad_id ?? creative.creative_name ?? `${index}`;
                    const thumbnailUrl =
                      firstAd?.creative?.thumbnail_url ||
                      creative.creative_thumbnail;
                    const hasError = imageErrors.has(creativeId);
                    const showUnavailable = !thumbnailUrl || hasError;

                    return (
                      <div
                        key={`${activeTab}-${creativeId}`}
                        onClick={() => handleCreativeSelect(creative)}
                        className={`relative w-[120px] h-[120px] aspect-square cursor-pointer rounded-[8px] bg-[#F6F6F6]`}
                      >
                        {showUnavailable ? (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <IconPhotoOff size={24} className="text-[#AAAAAA]" />
                          </div>
                        ) : (
                          <img
                            src={`${thumbnailUrl}${thumbnailUrl.includes("?") ? "&" : "?"}cb=${imageKey}`}
                            alt={creative.creative_name}
                            className="w-full h-full object-cover rounded-[8px]"
                            onError={() => handleImageError(creativeId)}
                          />
                        )}
                        {activeCreative != null &&
                          ((activeCreative.ads?.[0] ?? activeCreative.first_ad_metadata)?.ad_id ?? activeCreative.creative_name) ===
                          creativeId ? (
                          <div className="absolute inset-0 bg-black/70 rounded-[8px]" />
                        ) : (
                          <div className="absolute inset-0 bg-black/60 opacity-0 hover:opacity-100 flex flex-col justify-around p-4 rounded-[8px]">
                            {/* Spend */}
                            <div className="flex flex-col items-center justify-center text-[11px]">
                              <div className="text-white/80 font-normal">Spend</div>
                              <div className="text-white font-medium">
                                <span className="">
                                  {currentTabData?.columns[0]?.prefix}
                                  {creative?.[
                                    currentTabData?.columns[0]?.key as string
                                  ]?.toLocaleString() ?? "—"}
                                  {currentTabData?.columns[0]?.suffix}
                                </span>
                              </div>
                            </div>
                            {/* All Time Rank */}
                            <div className="flex flex-col items-center justify-center text-[11px]">
                              <div className="text-white/80 font-normal">
                                All Time Rank
                              </div>
                              <div className="text-white font-medium">
                                <span className="">#{creative.all_time_rank}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Creative Details Dialog */}
      <CreativeDetailsDialog
        open={showDetailsDialog}
        onOpenChange={setShowDetailsDialog}
        columnData={currentTabData?.columns}
        creative={activeCreative}
        defaultMetricId={selectedMetric}
        metricColumns={data?.creative_metric_columns}
        reportMetadata={reportMetadata}
      />
    </OverviewSectionWrapper>
  );
};

export default CreativeOverview;
